import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react';
import _ from 'lodash';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useDocumentTitle, useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import htmlParser from 'html-react-parser';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { WMICUserAccessUtil, WMICLogger, JURISDICTIONS, LINE_OF_BUSINESS, ERROR_CODE } from 'wmic-pe-portals-utils-js';
import { LoadSaveService } from 'wmic-pe-capability-gateway-quoteandbind';
import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';
import { PolicyService } from 'wmic-pe-capability-gateway-policy';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';

import { WMICValidationMessageModal } from 'wmic-pe-capability-gateway-common-react';
import { WMICScrollToError } from 'wmic-pe-components-platform-react';
import WMICPaymentPlansList from './WMICPaymentPlansList/WMICPaymentPlansList';
import WMICMonthlyAutomatedForm from './WMICMonthlyAutomatedForm/WMICMonthlyAutomatedFormComponent';
import WMICUIIssuesModal from './WMICUIIssuesModal/WMICUIIssuesModal';
import metadata from './WMICPaymentPage.metadata.json5';

import messages from './WMICPaymentPage.messages';
import styles from './WMICPaymentPage.module.scss';
import { TranslatorContext } from '@jutro/legacy/locale';

const DIRECTBILL_CODE = 'DirectBill';
const AGENCYBILL_CODE = 'AgencyBill';
const CREDITCARD_CODE = 'CREDITCARD';
const CHEQUE_CODE = 'CHEQUE';
const UNPAID_CODE = 'UNPAID';

function WMICPaymentPage(props) {
    const { wizardData: submissionVM, updateWizardData, history } = props;
    const translator = useContext(TranslatorContext);
    const { isComponentValid, onValidate, disregardFieldValidation } = useValidation('WMICPaymentPage');
    const { setWizardLoading, showError, showCustom } = useWizardModals();
    const { authUserData: currentUser, authHeader } = useAuthentication();

    const [showErrors, setShowErrors] = useState(false);
    const [scrollToError, setScrollToError] = useState();
    const [monthlyAutomatedId, setMonthlyAutomatedId] = useState();
    const [monthlyAutomatedSelected, setMonthlyAutomatedSelected] = useState(false);
    const [paymentPlanSelected, setPaymentPlanSelected] = useState(false);
    const eDeliveryEnabled = _.get(submissionVM, 'edeliveryEnabled_WMIC.value', false);
    const eDeliveryEnrolled = _.get(submissionVM, 'edeliveryEnrolled_WMIC.value', false);
    const defaultFirstDateOfMonth = _.get(submissionVM, 'bindData.paymentDetails.firstDateOfMonth', 1);
    const isCP = _.get(submissionVM, 'lob.value.code') === LINE_OF_BUSINESS.GENERAL_LIABILITY;
    const selectedBillingMethod = submissionVM?.bindData?.billingMethod?.value?.code;

    useDocumentTitle(translator(messages.paymentTitle), submissionVM);

    const eDeliveryMessagesVisible = () =>
        eDeliveryEnabled && eDeliveryEnrolled && selectedBillingMethod === AGENCYBILL_CODE;

    const canBindSubmission = useMemo(
        () => WMICUserAccessUtil.canBindSubmission(currentUser.permission_Ext),
        [currentUser.permission_Ext]
    );

    useEffect(() => {
        const paymentPlans = _.get(submissionVM, 'bindData.paymentPlans.value', []);
        const monthlyAutomated = _.find(paymentPlans, { isMontlyInstallments: true });
        if (monthlyAutomated) {
            setMonthlyAutomatedId(monthlyAutomated.billingId);
        }

        // We want to remove any defaulted payment plans when first loading the page
        _.set(submissionVM, 'bindData.selectedPaymentPlan', null);
        setPaymentPlanSelected(false);
    }, []);

    const selectedPaymentPlanChanged = selectedPaymentPlan => {
        if (selectedPaymentPlan) {
            if (selectedPaymentPlan === monthlyAutomatedId) {
                _.set(submissionVM, 'bindData.paymentDetails.paymentMethod.value', 'WIRE');
            } else {
                disregardFieldValidation('monthlyAutomatedForm');
                _.set(submissionVM, 'bindData.paymentDetails.paymentMethod.value', null);
                _.set(submissionVM, 'bindData.paymentDetails.firstDateOfMonth.value', defaultFirstDateOfMonth);
            }
            updateWizardData(submissionVM);
            setMonthlyAutomatedSelected(selectedPaymentPlan === monthlyAutomatedId);
            setShowErrors(false);
            setPaymentPlanSelected(true);
        }
    };

    const getNextLabel = () => {
        const paymentMethod = _.get(submissionVM, 'bindData.paymentDetails.paymentMethod.value');
        return paymentMethod === CREDITCARD_CODE ? messages.submitPayment : messages.bindPolicy;
    };

    const setDefaultPaymentDetailsInfo = useCallback(() => {
        submissionVM.bindData.paymentDetails.emailAddress.value =
            submissionVM.baseData.accountHolder.emailAddress1.value;
        submissionVM.bindData.paymentDetails.recieveEmailReceipt.value = false;
    }, [submissionVM]);

    const mockCallReceiptpage = useCallback(() => {
        const submissionNumber = submissionVM.quoteID.value;
        const postalCode = submissionVM.baseData.accountHolder.primaryAddress.postalCode.value;
        const datetime = '2022-10-07 10:13:27';
        const name = 'john smith';
        const refNumber = '63404216A0E2B3B734A01DF92AB52F38F16853C3';
        const authCode = 'tst682';
        const mPan = 'XXXXXXXXXXXX0009';
        const card = 'Mastercard';
        const emailAddress = 'Something@email.org';
        history.push(
            `/receipt/${submissionNumber}/${postalCode}/${datetime}/${name}/${refNumber}/${authCode}/${mPan}/${card}/${emailAddress}`
        );
    }, [submissionVM, history]);

    const proceedToCreditCardPayment = useCallback(() => {
        const selectedPaymentPlan = _.find(
            submissionVM.bindData.paymentPlans.value,
            plan => plan.billingId === submissionVM.bindData.selectedPaymentPlan.value
        );
        return LoadSaveService.retrieveCreditCardPaymentService(
            submissionVM.quoteID.value,
            selectedPaymentPlan.downPayment,
            submissionVM.bindData.paymentDetails.emailAddress.value,
            authHeader
        ).then(paymentControllerURL => {
            if (window.location.hostname !== 'localhost') {
                window.location.href = paymentControllerURL;
            } else {
                mockCallReceiptpage();
            }
        });
    }, [submissionVM, authHeader, mockCallReceiptpage]);

    const prebind = useCallback(() => {
        if (submissionVM.bindData.paymentDetails.autoRecurringPayer_WMIC) {
            submissionVM.bindData.paymentDetails.autoRecurringPayer_WMIC.value = null;
        }
        return LoadSaveService.prebindSubmission(submissionVM.value, authHeader).then(proceedToCreditCardPayment);
    }, [submissionVM, authHeader, proceedToCreditCardPayment]);

    const initDirectPayment = submission => {
        _.set(submission, 'bindData.paymentDetails', null);
    };

    const initAgentBillPayment = submission => {
        _.set(submission, 'bindData.selectedPaymentPlan', null);
        _.set(submission, 'bindData.paymentDetails', null);
        _.set(submission, 'bindData.paymentPlans', []);
    };

    const getPolicyNumber = useCallback(
        async policyNumber => {
            let returnPolicyNum;
            await PolicyService.getPolicyTerms(policyNumber, authHeader)
                .then(response => {
                    returnPolicyNum = response.policyNumber;
                })
                .catch(() => {
                    returnPolicyNum = false;
                });
            return returnPolicyNum;
        },
        [authHeader]
    );

    const showUWIssueModal = useCallback(
        (uwIssues, jobId, isValidationIssues) => {
            showCustom(<WMICUIIssuesModal uiIssues={uwIssues} isValidationIssues={isValidationIssues} />)
                .then(() => {
                    history.push(`/quotes/${jobId}/summary`);
                })
                .catch(() => {
                    _.noop();
                });
        },
        [history, showCustom]
    );

    const submitPolicy = useCallback(async () => {
        const selectedPaymentPlan = _.find(
            submissionVM.bindData.paymentPlans.value,
            plan => plan.billingId === submissionVM.bindData.selectedPaymentPlan.value
        );
        const submissionCopy = _.cloneDeep(submissionVM.value);
        if (selectedBillingMethod === AGENCYBILL_CODE) {
            initAgentBillPayment(submissionCopy);
        } else if (
            selectedBillingMethod === DIRECTBILL_CODE &&
            selectedPaymentPlan.isMontlyInstallments === false &&
            submissionVM.bindData.paymentDetails.paymentMethod.value === CHEQUE_CODE
        ) {
            initDirectPayment(submissionCopy);
        }

        return LoadSaveService.bindSubmission(submissionCopy, authHeader).then(async submission => {
            const { policyNumber } = submission.bindData;
            if (!_.isNil(policyNumber)) {
                let retrPol = await getPolicyNumber(policyNumber);
                let servicecounter = 1;
                while (servicecounter < 5 && retrPol === false) {
                    retrPol = await getPolicyNumber(policyNumber);
                    servicecounter++;
                }
                if (retrPol !== false) {
                    history.push(`/policies/${submission.bindData.policyNumber}/1/summary`);
                } else {
                    WMICLogger.error('Error getting policy number');
                    history.push('/home');
                }
            } else {
                submissionVM.value = submission;
                const paymentMethod = _.get(submissionVM, 'bindData.paymentDetails.paymentMethod.value');
                if (!paymentMethod) {
                    _.set(submissionVM, 'bindData.paymentDetails.value', { paymentMethod: UNPAID_CODE });
                }
                updateWizardData(submissionVM);
            }
        });
    }, [authHeader, getPolicyNumber, history, selectedBillingMethod, submissionVM, updateWizardData]);

    /**
     * Update submission and then navigate to quote summary screen
     */
    const onNext = useCallback(async () => {
        try {
            if (selectedBillingMethod === DIRECTBILL_CODE && !paymentPlanSelected) {
                showCustom(
                    <WMICValidationMessageModal
                        message={messages.pleaseSelectPaymentPlan}
                        isOpen={false}
                        onResolve={() => {}}
                    />
                );
                return false;
            }
            if (!isComponentValid) {
                setShowErrors(true);
                setScrollToError(Date.now());
                return false;
            }
            setWizardLoading(true);
            setDefaultPaymentDetailsInfo();

            const paymentMethod = _.get(submissionVM, 'bindData.paymentDetails.paymentMethod.value');
            if (paymentMethod === CREDITCARD_CODE) {
                await prebind();
            } else {
                await submitPolicy();
            }
            return submissionVM;
        } catch (error) {
            WMICLogger.error('WMICPaymentPage onNext', error);
            if (error.errorCode === ERROR_CODE.GW_UNDERWRITING_ERROR) {
                const uwIssues = WMICErrorHandler.extractUWIssuesFromErrorMessage(error);
                showUWIssueModal(uwIssues, submissionVM.quoteID.value);
            } else if (_.get(error, 'baseError', '').toLowerCase().includes('entityvalidationexception')) {
                const uiIssues = WMICErrorHandler.extractValidationIssuesFromErrorMessage(error);
                showUWIssueModal(uiIssues, submissionVM.quoteID.value, true);
            } else {
                showError({
                    title: translator(messages.error),
                    message: translator(messages.bindingFailure),
                });
            }
            return false;
        } finally {
            setWizardLoading(false);
        }
    }, [
        isComponentValid,
        paymentPlanSelected,
        prebind,
        selectedBillingMethod,
        setDefaultPaymentDetailsInfo,
        setWizardLoading,
        showCustom,
        showError,
        showUWIssueModal,
        submissionVM,
        submitPolicy,
        translator,
    ]);

    const overrideProps = {
        '@field': {
            parentNode: submissionVM,
        },
        paymentPlansContainer: {
            visible: selectedBillingMethod === DIRECTBILL_CODE,
        },
        paymentPlansList: {
            quoteID: _.get(submissionVM, 'quoteID.value'),
            postalCode: _.get(submissionVM, 'baseData.accountHolder.primaryAddress.postalCode.value'),
            authHeader,
            selectedPaymentPlanChanged,
        },
        electronicDocumentDistributionNotification: {
            visible: eDeliveryMessagesVisible(),
        },
        electronicDocumentDistributionNotificationContent: {
            content: htmlParser(translator(messages.electronicDocumentDistribution)),
        },
        waivingBrokerFeeNotification: {
            visible:
                isCP &&
                selectedBillingMethod === AGENCYBILL_CODE &&
                _.get(submissionVM, 'baseData.baseState.value.code') === JURISDICTIONS.QUEBEC,
        },
        waivingBrokerFeeNotificationContent: {
            content: htmlParser(translator(messages.waivingBrokerFee)),
        },
        paymentOptionsContainer: {
            visible: paymentPlanSelected && selectedBillingMethod === DIRECTBILL_CODE,
        },
        paymentMethod: {
            visible: !monthlyAutomatedSelected,
        },
        creditCardLeavingNote: {
            visible: _.get(submissionVM, 'bindData.paymentDetails.paymentMethod.value') === CREDITCARD_CODE,
        },
        chequeNote: {
            visible: _.get(submissionVM, 'bindData.paymentDetails.paymentMethod.value') === CHEQUE_CODE,
        },
        monthlyAutomatedForm: {
            visible: monthlyAutomatedSelected,
            showErrors,
            submissionVM,
            updateWizardData,
            onValidate,
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICPaymentPlansList,
            WMICMonthlyAutomatedForm,
        },
        resolveClassNameMap: styles,
    };

    return (
        <WMICWizardSubmissionPage
            nextLabel={getNextLabel()}
            onNext={onNext}
            showNext={canBindSubmission}
            cancelLabel={translator(commonMessages.saveAndExit)}
        >
            <WMICScrollToError counter={scrollToError} />
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICPaymentPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    ...wizardProps,
};

export default withRouter(WMICPaymentPage);
